import z from "zod";
import type { PermissionRole } from "@remi/types/permissions";

import type { PanelInvariantErrorType } from "../../../remi-error/errorTypes/quoting";
import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const adminErrorTypeValues = [
  "CANNOT_DISABLE_SELF",
  "CANNOT_MERGE_ADDRESS_INTO_ITSELF",
  "CANNOT_MERGE_INTO_DELETED_ADDRESS",
  "CANNOT_GENERATE_API_KEY_FOR_DISABLED_USER",
  "CANNOT_GENERATE_API_KEY_FOR_NON_INTERNAL_USER",
  "API_KEY_NOT_FOUND",
  "PORTAL_RESET_BID_NOT_FOUND",
  "PORTAL_RESET_NOT_STRATEGIC_PARTNER",
  "PORTAL_RESET_WORK_ORDER_NOT_ACCEPTED",
  "PORTAL_RESET_NTP_ALREADY_RECEIVED",
  "PORTAL_RESET_PAYKEEPER_CANNOT_CANCEL",
  "ROLE_ASSIGNMENT_ORG_RESTRICTED",
  "MANUAL_QUOTE_PANEL_EDIT_INVALID",
  "ATTACH_PROJECTS_RFQ_NOT_FOUND",
  "ATTACH_PROJECTS_BID_SCOPE_MISMATCH",
  "HISTORY_BACKFILL_TIMESTAMP_NOT_A_DATE",
  "HISTORY_BACKFILL_INSERT_FAILED",
  "HISTORY_BACKFILL_VERIFICATION_FAILED",
  "HISTORY_BACKFILL_RECHECK_REQUIRED",
  "HISTORY_BACKFILL_NO_JOIN_COLUMNS",
  "HISTORY_BACKFILL_CURSOR_NOT_ADVANCING",
  "HISTORY_BACKFILL_PROBE_MISSING_JOIN_COLUMN",
  "HISTORY_BACKFILL_UNKNOWN_MODE",
  "HISTORY_OUTBOX_EVENT_REJECTED",
  "HISTORY_OUTBOX_SEND_TIMED_OUT",
  "HISTORY_OUTBOX_POISON_LIMIT_EXCEEDED",
  "SLA_EVENT_PAYLOAD_INVALID",
  "SLA_EVENT_WRITE_INCOMPLETE",
  "SLA_INSTANCE_DEFINITION_MISSING",
  "SLA_DEFINITION_NOT_FOUND",
  "SLA_DEFINITION_INVALID",
  "DISPATCH_BOARD_SEED_FORBIDDEN_IN_PRODUCTION",
  "DISPATCH_BOARD_SEED_INCOMPLETE",
  "DISPATCH_BOARD_SEED_VISIT_NOT_FOUND",
  "DISPATCH_BOARD_SEED_VISIT_NOT_AUTO_SCHEDULED",
  "NOMAD_SEED_ADDRESS_NOT_COVERED",
] as const;
const adminErrorTypeSchema = z.enum(adminErrorTypeValues);
const AdminErrorType = adminErrorTypeSchema.enum;
type AdminErrorType = z.infer<typeof adminErrorTypeSchema>;

export type AdminErrors =
  | {
      errorType: typeof AdminErrorType.CANNOT_DISABLE_SELF;
      data: { personId: string };
    }
  | {
      errorType: typeof AdminErrorType.CANNOT_MERGE_ADDRESS_INTO_ITSELF;
      data: { sourceAddressId: string; targetAddressId: string };
    }
  | {
      errorType: typeof AdminErrorType.CANNOT_MERGE_INTO_DELETED_ADDRESS;
      data: { sourceAddressId: string; targetAddressId: string };
    }
  | {
      errorType: typeof AdminErrorType.CANNOT_GENERATE_API_KEY_FOR_DISABLED_USER;
      data: { personId: string };
    }
  | {
      errorType: typeof AdminErrorType.CANNOT_GENERATE_API_KEY_FOR_NON_INTERNAL_USER;
      data: { personId: string; organizationType: string };
    }
  | {
      errorType: typeof AdminErrorType.API_KEY_NOT_FOUND;
      data: { personId: string; tokenId: string };
    }
  | {
      errorType: typeof AdminErrorType.PORTAL_RESET_BID_NOT_FOUND;
      data: { bidId: number };
    }
  | {
      errorType: typeof AdminErrorType.PORTAL_RESET_NOT_STRATEGIC_PARTNER;
      data: { bidId: number };
    }
  | {
      errorType: typeof AdminErrorType.PORTAL_RESET_WORK_ORDER_NOT_ACCEPTED;
      data: { bidId: number };
    }
  | {
      errorType: typeof AdminErrorType.PORTAL_RESET_NTP_ALREADY_RECEIVED;
      data: { bidId: number };
    }
  | {
      errorType: typeof AdminErrorType.PORTAL_RESET_PAYKEEPER_CANNOT_CANCEL;
      data: { bidId: number; requestForQuoteId: number };
    }
  | {
      errorType: typeof AdminErrorType.ROLE_ASSIGNMENT_ORG_RESTRICTED;
      data: { actorPersonId: string; targetPersonId: string; rejectedRoles: PermissionRole[] };
    }
  | {
      errorType: typeof AdminErrorType.MANUAL_QUOTE_PANEL_EDIT_INVALID;
      data: {
        requestForQuoteId: number;
        invariant: PanelInvariantErrorType;
      };
    }
  | {
      errorType: typeof AdminErrorType.ATTACH_PROJECTS_RFQ_NOT_FOUND;
      data: { requestForQuoteId: number };
    }
  | {
      errorType: typeof AdminErrorType.ATTACH_PROJECTS_BID_SCOPE_MISMATCH;
      data: { requestForQuoteId: number; bidId: number; rejectedProjectIds: number[] };
    }
  | {
      errorType: typeof AdminErrorType.HISTORY_BACKFILL_TIMESTAMP_NOT_A_DATE;
      data: { entity: string; field: string };
    }
  | {
      errorType: typeof AdminErrorType.HISTORY_BACKFILL_INSERT_FAILED;
      data: { entity: string; rowCount: number };
    }
  | {
      errorType: typeof AdminErrorType.HISTORY_BACKFILL_VERIFICATION_FAILED;
      data: { entity: string; mode: string; remainingGaps: number };
    }
  | {
      errorType: typeof AdminErrorType.HISTORY_BACKFILL_RECHECK_REQUIRED;
      data: { entity: string };
    }
  | {
      errorType: typeof AdminErrorType.HISTORY_BACKFILL_NO_JOIN_COLUMNS;
      data: { entity: string };
    }
  | {
      errorType: typeof AdminErrorType.HISTORY_BACKFILL_CURSOR_NOT_ADVANCING;
      data: { entity: string };
    }
  | {
      errorType: typeof AdminErrorType.HISTORY_BACKFILL_PROBE_MISSING_JOIN_COLUMN;
      data: { entity: string; column: string };
    }
  | {
      errorType: typeof AdminErrorType.HISTORY_BACKFILL_UNKNOWN_MODE;
      data: { mode: string; allowed: string };
    }
  | {
      errorType: typeof AdminErrorType.HISTORY_OUTBOX_EVENT_REJECTED;
      data: {
        historyTable: string;
        sourceEventId: string;
        eventName: string;
        reason: string;
        historyId: string;
        entityId: string;
        changeGroupId: string;
      };
    }
  | {
      errorType: typeof AdminErrorType.HISTORY_OUTBOX_SEND_TIMED_OUT;
      data: { historyTable: string; eventCount: number; timeoutMs: number };
    }
  | {
      errorType: typeof AdminErrorType.HISTORY_OUTBOX_POISON_LIMIT_EXCEEDED;
      data: { historyTable: string; poisonEventCount: number; limit: number };
    }
  | {
      errorType: typeof AdminErrorType.SLA_EVENT_PAYLOAD_INVALID;
      data: { invalidCount: number; issueCodes: string[]; invalidSourceEventIds: string[] };
    }
  | {
      errorType: typeof AdminErrorType.SLA_EVENT_WRITE_INCOMPLETE;
      data: { expectedCount: number; missingCount: number; missingSourceEventIds: string[] };
    }
  | {
      errorType: typeof AdminErrorType.SLA_INSTANCE_DEFINITION_MISSING;
      data: { slaInstanceId: string | null; slaDefinitionId: string };
    }
  | {
      errorType: typeof AdminErrorType.SLA_DEFINITION_NOT_FOUND;
      data: { slaDefinitionId: string };
    }
  | {
      errorType: typeof AdminErrorType.SLA_DEFINITION_INVALID;
      data: { slaDefinitionId: string; issues: string[] };
    }
  | {
      errorType: typeof AdminErrorType.DISPATCH_BOARD_SEED_FORBIDDEN_IN_PRODUCTION;
      data: { deployEnvironment: string };
    }
  | {
      errorType: typeof AdminErrorType.DISPATCH_BOARD_SEED_INCOMPLETE;
      data: { seededCount: number; requestedCount: number; failures: string[] };
    }
  | {
      errorType: typeof AdminErrorType.DISPATCH_BOARD_SEED_VISIT_NOT_FOUND;
      data: { visitIndex: number; visitCount: number };
    }
  | {
      errorType: typeof AdminErrorType.DISPATCH_BOARD_SEED_VISIT_NOT_AUTO_SCHEDULED;
      data: { projectId: number; homeownerName: string; offered: string; siteInspectorId: string | null };
    }
  | {
      errorType: typeof AdminErrorType.NOMAD_SEED_ADDRESS_NOT_COVERED;
      data: { addressKey: string; contractorOrgId: string };
    };

export const adminErrorDetails: Record<AdminErrorType, ErrorDetails> = {
  CANNOT_DISABLE_SELF: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Admin attempted to disable their own account",
  },
  CANNOT_MERGE_ADDRESS_INTO_ITSELF: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Admin attempted to merge a property address into itself",
  },
  CANNOT_MERGE_INTO_DELETED_ADDRESS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Admin attempted to merge a property address into a soft-deleted target",
  },
  CANNOT_GENERATE_API_KEY_FOR_DISABLED_USER: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Admin attempted to generate an API key for a disabled user",
  },
  CANNOT_GENERATE_API_KEY_FOR_NON_INTERNAL_USER: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Admin attempted to generate an API key for a user outside the Remi organization",
  },
  API_KEY_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Admin attempted to expire or reactivate an API key that does not belong to the target user",
  },
  PORTAL_RESET_BID_NOT_FOUND: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "HO portal financing reset called with unknown bidId",
  },
  PORTAL_RESET_NOT_STRATEGIC_PARTNER: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "HO portal financing reset blocked: bid is not Strategic Partner",
  },
  PORTAL_RESET_WORK_ORDER_NOT_ACCEPTED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "HO portal financing reset blocked: homeowner has not accepted the work order yet",
  },
  PORTAL_RESET_NTP_ALREADY_RECEIVED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "HO portal financing reset blocked: NOTICE_TO_PROCEED task is already complete",
  },
  PORTAL_RESET_PAYKEEPER_CANNOT_CANCEL: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "HO portal financing reset failed: Paykeeper refused to cancel escrow",
  },
  ROLE_ASSIGNMENT_ORG_RESTRICTED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Role assignment blocked: a REMI-guarded role was assigned outside the Remi organization",
  },
  MANUAL_QUOTE_PANEL_EDIT_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Admin quote save blocked: hand-edited solar panel line items failed a panel invariant",
  },
  ATTACH_PROJECTS_RFQ_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Attach-projects-to-RFQ called with an unknown destination requestForQuoteId",
  },
  ATTACH_PROJECTS_BID_SCOPE_MISMATCH: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "Attach-projects-to-RFQ blocked: a supplied project does not belong to the destination RFQ's bid",
  },
  HISTORY_BACKFILL_TIMESTAMP_NOT_A_DATE: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "History backfill read a non-Date value from a timestamp column it needs",
  },
  HISTORY_BACKFILL_INSERT_FAILED: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "History backfill batch insert failed; the run is incomplete and must be re-run",
  },
  HISTORY_BACKFILL_VERIFICATION_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "History backfill left gaps its own post-write verification still detects",
  },
  HISTORY_BACKFILL_RECHECK_REQUIRED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "History backfill reconcile mode was called without the transactional recheck it requires",
  },
  HISTORY_BACKFILL_NO_JOIN_COLUMNS: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "History backfill derived no stable join column, so entities could not be told apart",
  },
  HISTORY_BACKFILL_CURSOR_NOT_ADVANCING: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "History backfill scan re-served the previous page, so its keyset cursor is inclusive",
  },
  HISTORY_BACKFILL_PROBE_MISSING_JOIN_COLUMN: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "History backfill history projection omitted a join column, so history could not be matched",
  },
  HISTORY_BACKFILL_UNKNOWN_MODE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "History backfill script invoked with an unrecognized MODE, which would run as a no-op",
  },
  HISTORY_OUTBOX_EVENT_REJECTED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Inngest rejected a history outbox event's payload, so its row was stamped without delivery",
  },
  HISTORY_OUTBOX_SEND_TIMED_OUT: {
    classification: ErrorClassification.TIMEOUT_ERROR,
    sentryDescription: "Sending a history outbox batch to Inngest exceeded its timeout",
  },
  HISTORY_OUTBOX_POISON_LIMIT_EXCEEDED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Too many history outbox events were rejected in one run, so the table was stopped",
  },
  SLA_EVENT_PAYLOAD_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "History events reached the SLA consumer with an invalid payload, so their SlaEvents were not recorded",
  },
  SLA_EVENT_WRITE_INCOMPLETE: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "SlaEvent inserts were silently dropped, most likely by a parent deleted mid-write",
  },
  SLA_INSTANCE_DEFINITION_MISSING: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "An SLA instance's definition was not among the loaded matchers during a rebuild",
  },
  SLA_DEFINITION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "A rebuild was requested for an SLA definition that does not exist or was deleted",
  },
  SLA_DEFINITION_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "An SLA definition edit was refused because its rules would not run",
  },
  DISPATCH_BOARD_SEED_FORBIDDEN_IN_PRODUCTION: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "The Nomad dispatch board seed was requested against production",
  },
  DISPATCH_BOARD_SEED_INCOMPLETE: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "The Nomad dispatch board seed finished with some visits unseeded",
  },
  DISPATCH_BOARD_SEED_VISIT_NOT_FOUND: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A dispatch board seed visit was requested by an index the seed does not define",
  },
  DISPATCH_BOARD_SEED_VISIT_NOT_AUTO_SCHEDULED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription:
      "Nomad's auto-scheduler did not book a dispatch board seed visit onto the fixture inspector in the window it was offered",
  },
  NOMAD_SEED_ADDRESS_NOT_COVERED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A Nomad seed address falls outside every inspector territory, so the board would place nothing",
  },
};
